import Link from "next/link";
import { storyblokEditable } from "@storyblok/react/rsc";

import { MaskShape, type MaskShapeVariant } from "@/components/illustrations/MaskShape";
import TopicIcon, { type TopicIconVariant } from "@/components/icons/TopicIcon";
import { cn } from "@/lib/utils";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import type { MultilinkStoryblok } from "@/component-types-sb";

type ShapeColor = "sun-100" | "sand-50" | "sand-100" | "sky-50" | "sky-75" | "sky-100" | "white";

const shapeColorClasses: Record<ShapeColor, string> = {
  "sun-100": "text-sun-100",
  "sand-50": "text-sand-50",
  "sand-100": "text-sand-100",
  "sky-50": "text-sky-50",
  "sky-75": "text-sky-75",
  "sky-100": "text-sky-100",
  white: "text-white",
};

type TopicCardStoryblok = {
  label?: string;
  icon?: TopicIconVariant;
  shape?: MaskShapeVariant;
  shape_color?: ShapeColor;
  link?: MultilinkStoryblok;
  _uid: string;
  component: "topic_card";
  [k: string]: any;
};

type TopicCardProps = {
  blok: TopicCardStoryblok;
};

const TopicCard = ({ blok }: TopicCardProps) => {
  const shape = (blok.shape || "four_vertical_even") as MaskShapeVariant;
  const shapeColor = (blok.shape_color || "sun-100") as ShapeColor;
  const colorClass = shapeColorClasses[shapeColor] || "text-sun-100";
  const href = getUrlFromLinkObject(blok.link) || null;

  const content = (
    <div
      className="group relative isolate flex flex-col items-center justify-center gap-1 size-[160px] rounded-xl p-4 cursor-pointer"
      {...storyblokEditable(blok)}
    >
      {/* Shape background */}
      <div className="absolute top-0 right-0 bottom-0 left-0 z-[1] transition duration-200 group-hover:brightness-90">
        <MaskShape
          variant={shape}
          className={cn("w-full h-full", colorClass)}
          preserveAspectRatio="none"
        />
      </div>

      {/* Icon */}
      {blok.icon && (
        <TopicIcon
          variant={blok.icon}
          className="relative z-[2] w-6 h-6"
        />
      )}

      {/* Label */}
      {blok.label && (
        <p className="relative z-[2] text-headline text-black text-center leading-[1.2]">
          {blok.label}
        </p>
      )}
    </div>
  );

  if (href) {
    return (
      <Link href={href} target={blok.link?.target || "_self"}>
        {content}
      </Link>
    );
  }

  return content;
};

export default TopicCard;
